import type { App } from 'vue'
import ElementPlus, {
  dialogProps,
  drawerProps,
  imageEmits,
  imageProps,
  imageViewerProps,
  ElDialog as OriginalDialog,
  ElDrawer as OriginalDrawer,
  ElImage as OriginalImage,
  ElImageViewer as OriginalImageViewer,
  ElMessage as originalMessage,
  ElMessageBox as originalMessageBox,
  ElNotification as originalNotification,
} from 'element-plus/es'
import { defineComponent, h, isVNode, ref, shallowRef, Teleport } from 'vue'
import { APP_OVERLAY_TARGET, resolveAppOverlayTarget } from './overlay'

export * from 'element-plus/es'

// 克隆公开组件的默认 Props，保留原始 setup、事件和公开方法，不修改第三方实例。
export const ElDialog = {
  ...OriginalDialog,
  props: { ...dialogProps, appendTo: { ...dialogProps.appendTo, default: APP_OVERLAY_TARGET } },
} as unknown as typeof OriginalDialog

export const ElDrawer = {
  ...OriginalDrawer,
  props: { ...drawerProps, appendTo: { ...drawerProps.appendTo, default: APP_OVERLAY_TARGET } },
} as unknown as typeof OriginalDrawer

export const ElImageViewer = defineComponent({
  name: 'AppImageViewer',
  inheritAttrs: false,
  setup(_, { attrs, slots, expose }) {
    const instance = shallowRef<InstanceType<typeof OriginalImageViewer>>()
    expose({ setActiveItem: (index: number) => instance.value?.setActiveItem(index) })
    return () => h(Teleport, { to: APP_OVERLAY_TARGET }, [
      h(OriginalImageViewer, { ...attrs, ref: instance, teleported: false }, slots),
    ])
  },
}) as unknown as typeof OriginalImageViewer

// Image 没有自定义预览目标，使用其公开 Props/事件组合独立 Viewer，避免内联预览被祖先裁剪。
export const ElImage = defineComponent({
  name: 'AppImage',
  inheritAttrs: false,
  props: imageProps,
  emits: imageEmits,
  setup(props, { attrs, slots, emit, expose }) {
    const open = ref(false)
    function showPreview() {
      if (props.previewSrcList.length) {
        open.value = true
        emit('show')
      }
    }
    expose({ showPreview })
    return () => [
      h(OriginalImage, {
        ...attrs,
        ...props,
        previewSrcList: [],
        style: [attrs.style, props.previewSrcList.length ? { cursor: 'pointer' } : undefined],
        onClick: (event: MouseEvent) => {
          if (event.target instanceof HTMLImageElement) {
            showPreview()
          }
          const listeners = Array.isArray(attrs.onClick) ? attrs.onClick : [attrs.onClick]
          for (const listener of listeners) {
            if (typeof listener === 'function') {
              listener(event)
            }
          }
        },
        onLoad: (event: Event) => emit('load', event),
        onError: (event: Event) => emit('error', event),
      }, slots),
      open.value && props.previewSrcList.length
        ? h(ElImageViewer, {
            ...Object.fromEntries(Object.keys(imageViewerProps).map(key => [key, (props as Record<string, unknown>)[key]])),
            urlList: props.previewSrcList,
            initialIndex: props.initialIndex < props.previewSrcList.length ? props.initialIndex : 0,
            onClose: () => {
              open.value = false
              emit('close')
            },
            onSwitch: (index: number) => emit('switch', index),
          }, { 'default': slots.viewer, 'toolbar': slots.toolbar, 'progress': slots.progress, 'viewer-error': slots['viewer-error'] })
        : null,
    ]
  },
}) as unknown as typeof OriginalImage

function overlayOptions(value: unknown) {
  const options = value && typeof value === 'object' && !isVNode(value)
    ? value as Record<string, unknown>
    : { message: value }
  return { ...options, appendTo: resolveAppOverlayTarget(options.appendTo as string | HTMLElement | undefined) }
}

function adaptService<T extends (...args: any[]) => any>(service: T, variants: string[], messageBox = false): T {
  return new Proxy(service, {
    apply(target, receiver, args) {
      return Reflect.apply(target, receiver, [overlayOptions(args[0]), ...args.slice(1)])
    },
    get(target, key, receiver) {
      const value = Reflect.get(target, key, receiver)
      if (typeof key !== 'string' || !variants.includes(key) || typeof value !== 'function') {
        return value
      }
      return (...args: unknown[]) => {
        if (messageBox) {
          const index = args[1] && typeof args[1] === 'object' ? 1 : 2
          args[index] = overlayOptions(args[index] ?? {})
        }
        else {
          args[0] = overlayOptions(args[0])
        }
        return Reflect.apply(value, target, args)
      }
    },
  })
}

export const ElMessage = adaptService(originalMessage, ['success', 'warning', 'info', 'error', 'primary'])
export const ElNotification = adaptService(originalNotification, ['success', 'warning', 'info', 'error'])
export const ElMessageBox = adaptService(originalMessageBox, ['alert', 'confirm', 'prompt'], true)

export default {
  ...ElementPlus,
  install(app: App, ...options: Parameters<typeof ElementPlus.install> extends [App, ...infer Options] ? Options : never) {
    ElementPlus.install(app, ...options)
    for (const [name, component] of Object.entries({ ElDialog, ElDrawer, ElImage, ElImageViewer })) {
      app.component(name, component)
    }
    Object.assign(app.config.globalProperties, {
      $message: ElMessage,
      $notify: ElNotification,
      $msgbox: ElMessageBox,
      $alert: ElMessageBox.alert,
      $confirm: ElMessageBox.confirm,
      $prompt: ElMessageBox.prompt,
    })
  },
}
